import { escapeHtml } from './_shared/htmlEscape.js'
import {
  parseCommitSections,
  textToNumberedHtml,
  textToBulletHtml,
} from './_shared/commitParsing.js'

/**
 * Generate Commit email HTML - standalone professional report.
 * This template does NOT use the shared emailWrapper because it needs
 * a different visual layout (technical update report) with its own
 * background color and brand bar.
 *
 * @param {Object} commit - Commit data from Firestore
 * @param {Object} senderInfo - Info about the user who sent the email
 * @param {string} [senderInfo.photoURL] - Profile image URL
 * @param {string} [senderInfo.displayName] - Display name for initials fallback
 * @returns {string} Complete standalone HTML email
 */
export function generateCommitEmailHtml(commit, senderInfo = {}) {
  // Parse commit body into structured sections
  const sections = parseCommitSections(commit.body)

  // Build the summary card content
  const summaryDescription = sections?.summary
    ? `<div style="font-size:15px; line-height:27px; color:#445063;">${escapeHtml(sections.summary)}</div>`
    : ''

  // Build work details section (only if present)
  const workSection = sections?.work
    ? `
          <!-- Work Details -->
          <tr>
            <td style="padding:0 34px 18px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border:1px solid #e2e8f0; border-radius:16px; background-color:#ffffff;">
                <tr>
                  <td style="padding:22px;">
                    <div style="font-size:18px; line-height:26px; font-weight:700; color:#142033; margin-bottom:14px;">
                      מה בוצע בפועל
                    </div>
                    <div style="font-size:15px; line-height:28px; color:#425066;">
                      ${textToNumberedHtml(sections.work)}
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`
    : ''

  // Build impact section (only if present)
  const impactSection = sections?.impact
    ? `
          <!-- Impact -->
          <tr>
            <td style="padding:0 34px 18px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#fcfcfd; border:1px solid #e6eaf1; border-radius:16px;">
                <tr>
                  <td style="padding:22px;">
                    <div style="font-size:18px; line-height:26px; font-weight:700; color:#142033; margin-bottom:14px;">
                      המשמעות התפעולית
                    </div>
                    ${sections.impactIntro ? `<div style="font-size:15px; line-height:28px; color:#425066; margin-bottom:10px;">${escapeHtml(sections.impactIntro)}</div>` : ''}
                    <div style="font-size:15px; line-height:28px; color:#425066;">
                      ${textToBulletHtml(sections.impact)}
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`
    : ''

  // Fallback for old-format commit bodies (no ## sections)
  const fallbackBody =
    sections?.raw && !sections.summary && !sections.work && !sections.impact
      ? `
          <!-- Legacy Body -->
          <tr>
            <td style="padding:0 34px 18px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border:1px solid #e2e8f0; border-radius:16px; background-color:#ffffff;">
                <tr>
                  <td style="padding:22px;">
                    <div style="font-size:15px; line-height:28px; color:#425066;">
                      <pre style="margin:0; white-space:pre-wrap; word-wrap:break-word; font-family:Arial, Helvetica, sans-serif; font-size:15px; line-height:28px; color:#425066;">${escapeHtml(sections.raw)}</pre>
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`
      : ''

  // Author signature
  const authorName = escapeHtml(senderInfo.displayName || commit.author || '')
  const authorInitial = escapeHtml((senderInfo.displayName || commit.author || '?').charAt(0))
  const avatarHtml = senderInfo.photoURL
    ? `<img src="${escapeHtml(senderInfo.photoURL)}" width="84" height="84" alt="Profile" style="display:block; border-radius:8px; object-fit:cover;" />`
    : `<div style="width:84px; height:84px; border-radius:8px; background:linear-gradient(135deg, #1e293b, #334155); color:#fff; font-size:32px; font-weight:700; line-height:84px; text-align:center;">${authorInitial}</div>`

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>עדכון טכני: ${escapeHtml(commit.subject)}</title>
</head>
<body style="margin:0; padding:0; background-color:#eef2f7; font-family:Arial, Helvetica, sans-serif; direction:rtl;">

  <!-- Preheader -->
  <div style="display:none; max-height:0; overflow:hidden; opacity:0; color:transparent;">
    עדכון טכני מקצועי על העבודה שבוצעה בפרויקט שלך, כולל תקציר, השפעה ותוצאות.
  </div>

  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#eef2f7; margin:0; padding:0;">
    <tr>
      <td align="center" style="padding:32px 12px;">

        <!-- Main Container -->
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="max-width:720px; background-color:#ffffff; border-radius:20px; overflow:hidden; border:1px solid #d9e1ec;">

          <!-- Top Brand Bar -->
          <tr>
            <td style="background:linear-gradient(135deg, #162338 0%, #253a5c 100%); padding:22px 28px; text-align:center;">
              <div style="font-size:30px; line-height:34px; font-weight:700; color:#ffffff; letter-spacing:0.3px;">
                GoFood
              </div>
              <div style="margin-top:8px; font-size:13px; line-height:20px; color:#cbd5e1;">
                שירות טכני · עדכוני פיתוח · דוחות ביצוע
              </div>
            </td>
          </tr>

          <!-- Hero / Title -->
          <tr>
            <td style="padding:34px 34px 18px 34px; background-color:#ffffff;">
              <div style="font-size:28px; line-height:36px; font-weight:700; color:#142033; text-align:center;">
                עדכון טכני שבוצע באפליקציה
              </div>
              <div style="margin-top:10px; font-size:15px; line-height:26px; color:#5b677a; text-align:center;">
                צוות הפיתוח שלנו השלים עבודה טכנית בפרויקט שלך ומצרף עבורך סיכום מסודר, ברור ומקצועי.
              </div>
            </td>
          </tr>

          <!-- Quick Summary Card -->
          <tr>
            <td style="padding:0 34px 18px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:linear-gradient(180deg,#f8fbff 0%,#f3f7fc 100%); border:1px solid #dbe6f2; border-radius:16px;">
                <tr>
                  <td style="padding:22px 22px 16px 22px;">
                    <div style="font-size:13px; line-height:20px; font-weight:700; color:#2d5baf; margin-bottom:8px;">
                      תקציר מנהלים
                    </div>
                    <div style="font-size:22px; line-height:30px; font-weight:700; color:#142033; margin-bottom:10px;">
                      ${escapeHtml(commit.subject)}
                    </div>
                    ${summaryDescription}
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          ${workSection}

          ${impactSection}

          ${fallbackBody}

          <!-- Stats -->
          <tr>
            <td style="padding:0 34px 22px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
                <tr>
                  <td colspan="3" style="padding-bottom:12px;">
                    <div style="font-size:18px; line-height:26px; font-weight:700; color:#142033;">
                      נתוני ביצוע
                    </div>
                  </td>
                </tr>
                <tr>
                  <td width="33.33%" style="padding-left:8px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#f0fdf4; border:1px solid #ccefd7; border-radius:14px;">
                      <tr>
                        <td style="padding:18px; text-align:center;">
                          <div style="font-size:24px; line-height:28px; font-weight:700; color:#166534;">+${commit.stats?.linesAdded || 0}</div>
                          <div style="margin-top:6px; font-size:13px; line-height:20px; color:#3d5b48;">שורות קוד נוספו</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td width="33.33%" style="padding:0 4px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#fef2f2; border:1px solid #f3caca; border-radius:14px;">
                      <tr>
                        <td style="padding:18px; text-align:center;">
                          <div style="font-size:24px; line-height:28px; font-weight:700; color:#b91c1c;">-${commit.stats?.linesDeleted || 0}</div>
                          <div style="margin-top:6px; font-size:13px; line-height:20px; color:#6d4d4d;">שורות קוד הוסרו</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                  <td width="33.33%" style="padding-right:8px;">
                    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background-color:#eff6ff; border:1px solid #cfe0ff; border-radius:14px;">
                      <tr>
                        <td style="padding:18px; text-align:center;">
                          <div style="font-size:24px; line-height:28px; font-weight:700; color:#1d4ed8;">${commit.stats?.filesChanged || 0}</div>
                          <div style="margin-top:6px; font-size:13px; line-height:20px; color:#49586e;">קבצים עודכנו</div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Completion Banner -->
          <tr>
            <td style="padding:0 34px 24px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:linear-gradient(180deg,#ecfdf5 0%,#e7f8ef 100%); border:1px solid #bfe8cc; border-radius:16px;">
                <tr>
                  <td style="padding:20px; text-align:center;">
                    <div style="font-size:20px; line-height:28px; font-weight:700; color:#166534;">
                      השדרוג הושלם בהצלחה ✅
                    </div>
                    <div style="margin-top:8px; font-size:14px; line-height:24px; color:#446152;">
                      השינויים עלו לאוויר באפליקציה ועודכנו בסביבת הפרויקט הרלוונטית.
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Personal Signature -->
          <tr>
            <td style="padding:0 34px 28px 34px;">
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="border-top:1px solid #e7ecf3; padding-top:18px;">
                <tr>
                  <td width="84" valign="middle" style="padding-top:18px; padding-left:16px;">
                    ${avatarHtml}
                  </td>
                  <td valign="middle" style="padding-top:18px;">
                    <div style="font-size:12px; line-height:18px; color:#7a8699; margin-bottom:4px;">
                      בוצע על ידי
                    </div>
                    <div style="font-size:20px; line-height:28px; font-weight:700; color:#142033;">
                      ${authorName}
                    </div>
                    <div style="font-size:14px; line-height:24px; color:#5e6a7d;">
                      צוות הפיתוח · GoFood
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background-color:#f8fafc; border-top:1px solid #e5ebf3; padding:20px 28px; text-align:center;">
              <div style="font-size:12px; line-height:20px; color:#7b8798;">
                מייל זה נשלח כחלק מדיווח טכני שוטף על עבודות פיתוח, תחזוקה ושדרוג בפרויקט שלך.
              </div>
              <div style="margin-top:6px; font-size:12px; line-height:20px; color:#98a2b3;">
                GoFood · Technical Service Updates
              </div>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`
}
